/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios";
import { useAuthStore } from "@/stores/authStore";

/** Modelo genérico de respuesta ApiResponse<T> con T = any por defecto */
export interface ApiResponse<T = any> {
  metadata?: MetadataNode;
  data?: T;
  dataTable?: unknown[];
  otherPayload?: unknown;
  counts?: CountsNode;
  status?: StatusNode;
  attachments?: unknown[];
  extras?: Record<string, unknown>;
}

export interface MetadataNode {
  userId: number;
  elapsed: string; // TimeSpan serializado como string
  lastUpdate: string; // ISO 8601 string
}

export interface CountsNode {
  totalCount?: number;
  totals?: Record<string, number>;
}

export interface StatusNode {
  success: boolean;
  message?: string;
  errors?: string[];
  statusCode?: number;
}

// Interfaces para notificaciones
export interface NotificationDto {
  idNotificacion: number;
  titulo: string;
  mensaje: string;
  icono: string;
  colorFondo: string;
  colorIcono: string;
  orden: number;
  prioridad: number;
  esPush: boolean;
  href?: string;
  leido: boolean;
  fechaLectura?: Date;
  fecha: Date;
  tiempoRelativo: string;
  tipoEnvio: string;
}

export interface UnreadCountResponse {
  unreadCount: number;
}

export interface CreateNotificationRequest {
  titulo: string;
  mensaje: string;
  icono?: string;
  colorFondo?: string;
  colorIcono?: string;
  orden?: number;
  prioridad?: number;
  esPush?: boolean;
  href?: string;
  fechaExpiracion?: Date;
}

export interface CreateUserNotificationRequest
  extends CreateNotificationRequest {
  usuariosIds: number[];
}

export interface CreateRoleNotificationRequest
  extends CreateNotificationRequest {
  rolesIds: number[];
}

export interface CreateCompanyNotificationRequest
  extends CreateNotificationRequest {
  empresasIds: number[];
}

export interface CreateSucursalNotificationRequest
  extends CreateNotificationRequest {
  sucursalesIds: number[];
}

export interface CreateGlobalNotificationRequest
  extends CreateNotificationRequest {
  usuariosExcluidos?: number[];
}

export interface CreateNotificationResponse {
  notificationId: number;
  affectedUsers?: number;
  usuariosIds?: number[];
}

// Nuevas interfaces para conexiones
export interface ConnectionData {
  id_usuario: number;
  nombre_usuario: string;
  email?: string;
  connection_id: string;
  dispositivo: string;
  navegador?: string;
  sistema_operativo?: string;
  tipo_dispositivo?: string;
  version_navegador?: string;
  ip_address?: string;
  latitud?: number;
  longitud?: number;
  pais?: string;
  ciudad?: string;
  tab_id?: string;
  fecha_conexion: string;
  fecha_desconexion?: string;
  esta_conectado: boolean;
  tiempo_sesion_minutos?: number;
}

export interface ConnectionHistoryData extends ConnectionData {
  id_historico: number;
  motivo_desconexion?: string;
}

export interface DeviceStatistics {
  categoria: string;
  subcategoria: string;
  usuarios_unicos: number;
  total_conexiones: number;
  duracion_promedio_minutos: number;
}

export interface GeographicStatistics {
  tipo_ubicacion: string;
  ubicacion: string;
  usuarios_unicos: number;
  total_conexiones: number;
  duracion_promedio_minutos: number;
  primera_conexion: string;
  ultima_conexion: string;
}

export interface ConnectionMapData {
  latitud: number;
  longitud: number;
  pais: string;
  ciudad: string;
  conexiones_activas: number;
  usuarios_unicos: number;
  usuarios_conectados: string;
  tiempo_promedio_sesion: number;
}

export interface SuspiciousConnectionData {
  id_usuario: number;
  nombre_usuario: string;
  email: string;
  paises_diferentes: number;
  primera_conexion_ventana: string;
  ultima_conexion_ventana: string;
  paises_detectados: string;
  ubicaciones_detectadas: string;
  tipo_sospecha: string;
  descripcion_alerta: string;
}

export interface CleanupConnectionsRequest {
  minutosInactividad?: number;
  ejecutarLimpieza?: boolean;
}

export interface UpdateGeolocationRequest {
  ipAddress: string;
  latitud: number;
  longitud: number;
  pais?: string;
  region?: string;
  ciudad?: string;
  codigoPais?: string;
  isp?: string;
  timezone?: string;
}

export interface ActivityReportData {
  // Por hora
  hora_del_dia?: number;
  // Por día de semana
  dia_semana_num?: number;
  dia_semana_nombre?: string;
  // Por fecha
  fecha?: string;
  // Campos comunes
  total_conexiones: number;
  usuarios_unicos: number;
  duracion_promedio_minutos: number;
  sesion_mas_larga_minutos?: number;
}

export interface RealtimeStatistics {
  estadisticas_generales: {
    usuarios_conectados_ahora: number;
    conexiones_activas_total: number;
    conexiones_escritorio: number;
    conexiones_movil: number;
    conexiones_tablet: number;
    conexiones_chrome: number;
    conexiones_firefox: number;
    conexiones_safari: number;
    conexiones_con_ubicacion: number;
    primera_conexion_activa: string;
    ultima_conexion_activa: string;
    tiempo_promedio_sesion_activa: number;
  };
  por_pais: Array<{
    pais: string;
    conexiones_activas: number;
    usuarios_unicos: number;
  }>;
  por_dispositivo: Array<{
    tipo_dispositivo: string;
    conexiones_activas: number;
  }>;
  por_navegador: Array<{
    navegador: string;
    conexiones_activas: number;
  }>;
  timestamp: string;
}

/**
 * Verifica si el token ha expirado comparando la fecha de expiración (UTC)
 * con la hora actual UTC
 */
const isTokenExpired = (expiresAt: string | null): boolean => {
  if (!expiresAt) return true;

  try {
    const expDate = new Date(expiresAt);
    const nowUtc = new Date();
    return expDate <= nowUtc;
  } catch (error) {
    console.error("Error al analizar la fecha de expiración del token:", error);
    return true; // Si hay error, considerar el token expirado
  }
};

/** Instancia de Axios con baseURL y JSON por defecto */
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Maneja la redirección al login cuando expira el token
 * Se define como función separada para evitar importaciones circulares
 */
let redirectToLogin: () => void = () => {
  window.location.href = "/login";
};

/**
 * Configura la función de redirección (llamar desde el componente principal)
 * @param redirectFn Función que maneja la redirección al login
 */
export const setRedirectFunction = (redirectFn: () => void) => {
  redirectToLogin = redirectFn;
};

/** Interceptor para verificar expiración del token y añadir el token Bearer */
apiClient.interceptors.request.use((config) => {
  const authStore = useAuthStore.getState();
  const { token, expiresAt, logout } = authStore;

  // Verificar si el token ha expirado
  if (token && isTokenExpired(expiresAt)) {
    // Token expirado, ejecutar logout y redireccionar
    logout();
    redirectToLogin();
    // Rechazar la petición para que no se envíe con token inválido
    return Promise.reject(
      new Error("Sesión expirada. Inicia sesión nuevamente.")
    );
  }

  // Token válido, añadirlo a la petición
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

/** Interceptor para manejar errores de respuesta 401/403 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Si es error de autenticación (401) o autorización (403)
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      // Ejecutar logout y redireccionar
      const { logout } = useAuthStore.getState();
      logout();
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);

/** Helper interno que ejecuta la petición y devuelve el ApiResponse<T> */
async function request<T = any>(
  method: string,
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  try {
    const isFormData = data instanceof FormData;
    const resp: AxiosResponse<ApiResponse<T>> = await apiClient.request({
      method,
      url,
      data,
      ...config,
      ...(isFormData ? { headers: { "Content-Type": "multipart/form-data" } } : {}),
    });
    return resp.data;
  } catch (error: any) {
    // Si el error ya fue manejado por los interceptores, solo lo propagamos
    if (error.message === "Sesión expirada. Inicia sesión nuevamente.") {
      throw error;
    }

    // Si es un error de axios con respuesta
    if (error.response) {
      // Crear un ApiResponse de error
      const errorResponse: ApiResponse<T> = {
        status: {
          success: false,
          message: error.response.data?.message || "Error en la petición",
          errors: [error.response.data?.error || error.message],
          statusCode: error.response.status,
        },
      };
      return errorResponse;
    }

    // Error genérico (red, etc)
    return {
      status: {
        success: false,
        message: "Error de conexión",
        errors: [error.message || "Error desconocido"],
        statusCode: 0,
      },
    };
  }
}

/** Cliente HTTP genérico */
export const api = {
  get<T = any>(url: string, config?: AxiosRequestConfig) {
    return request<T>("get", url, undefined, config);
  },
  post<TResponse = any, TRequest = TResponse>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig
  ) {
    return request<TResponse>("post", url, data, config);
  },
  put<TResponse = any, TRequest = TResponse>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig
  ) {
    return request<TResponse>("put", url, data, config);
  },
  delete<T = any>(url: string, config?: AxiosRequestConfig) {
    return request<T>("delete", url, undefined, config);
  },
  postForm<T = any>(
    url: string,
    formData: FormData,
    config?: AxiosRequestConfig
  ) {
    return request<T>("post", url, formData, config);
  },
  putForm<T = any>(
    url: string,
    formData: FormData,
    config?: AxiosRequestConfig
  ) {
    return request<T>("put", url, formData, config);
  },
};

/** Módulo de notificaciones integrado */
export const notifications = {
  // Obtener notificaciones del usuario
  async getNotifications(
    soloNoLeidas: boolean = false,
    limite: number = 50
  ): Promise<ApiResponse<NotificationDto[]>> {
    return api.get<NotificationDto[]>(
      `/api/notifications?soloNoLeidas=${soloNoLeidas}&limite=${limite}`
    );
  },

  // Obtener contador de no leídas
  async getUnreadCount(): Promise<ApiResponse<UnreadCountResponse>> {
    return api.get<UnreadCountResponse>("/api/notifications/unread-count");
  },

  // Marcar como leída
  async markAsRead(
    notificationId: number
  ): Promise<ApiResponse<{ success: boolean }>> {
    return api.put<{ success: boolean }>(
      `/api/notifications/${notificationId}/read`
    );
  },

  // Marcar todas como leídas
  async markAllAsRead(): Promise<
    ApiResponse<{ success: boolean; markedCount: number }>
  > {
    return api.put<{ success: boolean; markedCount: number }>(
      "/api/notifications/read-all"
    );
  },

  // Crear notificación para usuarios
  async createUserNotification(
    data: CreateUserNotificationRequest
  ): Promise<ApiResponse<CreateNotificationResponse>> {
    return api.post<any>("/api/notifications/users", data);
  },

  async createRoleNotification(
    data: CreateRoleNotificationRequest
  ): Promise<ApiResponse<CreateNotificationResponse>> {
    return api.post<any>("/api/notifications/roles", data);
  },

  async createCompanyNotification(
    data: CreateCompanyNotificationRequest
  ): Promise<ApiResponse<CreateNotificationResponse>> {
    return api.post<any>("/api/notifications/companies", data);
  },

  async createSucursalNotification(
    data: CreateSucursalNotificationRequest
  ): Promise<ApiResponse<CreateNotificationResponse>> {
    return api.post<any>("/api/notifications/sucursales", data);
  },

  async createGlobalNotification(
    data: CreateGlobalNotificationRequest
  ): Promise<ApiResponse<CreateNotificationResponse>> {
    return api.post<any>("/api/notifications/global", data);
  },

  // Eliminar notificación
  async deleteNotification(
    notificationId: number
  ): Promise<ApiResponse<{ success: boolean }>> {
    return api.delete<{ success: boolean }>(
      `/api/notifications/${notificationId}`
    );
  },

  // Obtener todas las notificaciones (admin)
  async getAllNotifications(
    pagina: number = 1,
    tamanoPagina: number = 50,
    filtro?: string
  ): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams({
      pagina: pagina.toString(),
      tamanoPagina: tamanoPagina.toString(),
    });

    if (filtro) {
      params.append("filtro", filtro);
    }

    return api.get<any[]>(`/api/notifications/all?${params}`);
  },
};

/** Nuevo módulo de conexiones */
export const connections = {
  // Obtener conexiones activas
  async getActiveConnections(params?: {
    tipoDispositivo?: string;
    pais?: string;
    desdeHace?: Date;
    solo_conectados?: string;
  }): Promise<ApiResponse<ConnectionData[]>> {
    const searchParams = new URLSearchParams();

    if (params?.tipoDispositivo) {
      searchParams.append("tipoDispositivo", params.tipoDispositivo);
    }
    if (params?.pais) {
      searchParams.append("pais", params.pais);
    }
    if (params?.desdeHace) {
      searchParams.append("desdeHace", params.desdeHace.toISOString());
    }
    if (params?.solo_conectados) {
      searchParams.append(
        "solo_conectados",
        params.solo_conectados == "true" ? "1" : "0"
      );
    }

    const queryString = searchParams.toString();
    const url = `/api/connections/active${
      queryString ? `?${queryString}` : ""
    }`;

    return api.get<ConnectionData[]>(url);
  },

  // Obtener historial de conexiones
  async getConnectionHistory(params?: {
    usuarioId?: number;
    fechaDesde?: Date;
    fechaHasta?: Date;
    pagina?: number;
    tamanoPagina?: number;
  }): Promise<ApiResponse<ConnectionHistoryData[]>> {
    const searchParams = new URLSearchParams();

    if (params?.usuarioId) {
      searchParams.append("usuarioId", params.usuarioId.toString());
    }
    if (params?.fechaDesde) {
      searchParams.append("fechaDesde", params.fechaDesde.toISOString());
    }
    if (params?.fechaHasta) {
      searchParams.append("fechaHasta", params.fechaHasta.toISOString());
    }
    if (params?.pagina) {
      searchParams.append("pagina", params.pagina.toString());
    }
    if (params?.tamanoPagina) {
      searchParams.append("tamanoPagina", params.tamanoPagina.toString());
    }

    const queryString = searchParams.toString();
    const url = `/api/connections/history${
      queryString ? `?${queryString}` : ""
    }`;

    return api.get<ConnectionHistoryData[]>(url);
  },

  // Obtener estadísticas de dispositivos
  async getDeviceStatistics(params?: {
    fechaDesde?: Date;
    fechaHasta?: Date;
  }): Promise<ApiResponse<DeviceStatistics[]>> {
    const searchParams = new URLSearchParams();

    if (params?.fechaDesde) {
      searchParams.append("fechaDesde", params.fechaDesde.toISOString());
    }
    if (params?.fechaHasta) {
      searchParams.append("fechaHasta", params.fechaHasta.toISOString());
    }

    const queryString = searchParams.toString();
    const url = `/api/connections/stats/devices${
      queryString ? `?${queryString}` : ""
    }`;

    return api.get<DeviceStatistics[]>(url);
  },

  // Obtener estadísticas geográficas
  async getGeographicStatistics(params?: {
    fechaDesde?: Date;
    fechaHasta?: Date;
    topCantidad?: number;
  }): Promise<ApiResponse<GeographicStatistics[]>> {
    const searchParams = new URLSearchParams();

    if (params?.fechaDesde) {
      searchParams.append("fechaDesde", params.fechaDesde.toISOString());
    }
    if (params?.fechaHasta) {
      searchParams.append("fechaHasta", params.fechaHasta.toISOString());
    }
    if (params?.topCantidad) {
      searchParams.append("topCantidad", params.topCantidad.toString());
    }

    const queryString = searchParams.toString();
    const url = `/api/connections/stats/geographic${
      queryString ? `?${queryString}` : ""
    }`;

    return api.get<GeographicStatistics[]>(url);
  },

  // Obtener mapa de conexiones
  async getConnectionsMap(): Promise<ApiResponse<ConnectionMapData[]>> {
    return api.get<ConnectionMapData[]>("/api/connections/map");
  },

  // Obtener historial de sesiones de usuario específico
  async getUserSessionHistory(
    userId: number,
    params?: {
      fechaDesde?: Date;
      fechaHasta?: Date;
      pagina?: number;
      tamanoPagina?: number;
    }
  ): Promise<ApiResponse<ConnectionHistoryData[]>> {
    const searchParams = new URLSearchParams();

    if (params?.fechaDesde) {
      searchParams.append("fechaDesde", params.fechaDesde.toISOString());
    }
    if (params?.fechaHasta) {
      searchParams.append("fechaHasta", params.fechaHasta.toISOString());
    }
    if (params?.pagina) {
      searchParams.append("pagina", params.pagina.toString());
    }
    if (params?.tamanoPagina) {
      searchParams.append("tamanoPagina", params.tamanoPagina.toString());
    }

    const queryString = searchParams.toString();
    const url = `/api/connections/user/${userId}/sessions${
      queryString ? `?${queryString}` : ""
    }`;

    return api.get<ConnectionHistoryData[]>(url);
  },

  // Obtener conexiones sospechosas
  async getSuspiciousConnections(params?: {
    horasVentana?: number;
    maxPaisesDiferentes?: number;
    maxDistanciaKm?: number;
  }): Promise<ApiResponse<SuspiciousConnectionData[]>> {
    const searchParams = new URLSearchParams();

    if (params?.horasVentana) {
      searchParams.append("horasVentana", params.horasVentana.toString());
    }
    if (params?.maxPaisesDiferentes) {
      searchParams.append(
        "maxPaisesDiferentes",
        params.maxPaisesDiferentes.toString()
      );
    }
    if (params?.maxDistanciaKm) {
      searchParams.append("maxDistanciaKm", params.maxDistanciaKm.toString());
    }

    const queryString = searchParams.toString();
    const url = `/api/connections/suspicious${
      queryString ? `?${queryString}` : ""
    }`;

    return api.get<SuspiciousConnectionData[]>(url);
  },

  // Limpiar conexiones inactivas
  async cleanupInactiveConnections(
    data: CleanupConnectionsRequest
  ): Promise<ApiResponse<any>> {
    return api.post<any>("/api/connections/cleanup", data);
  },

  // Obtener reporte de actividad
  async getActivityReport(params?: {
    fechaDesde?: Date;
    fechaHasta?: Date;
  }): Promise<ApiResponse<ActivityReportData[]>> {
    const searchParams = new URLSearchParams();

    if (params?.fechaDesde) {
      searchParams.append("fechaDesde", params.fechaDesde.toISOString());
    }
    if (params?.fechaHasta) {
      searchParams.append("fechaHasta", params.fechaHasta.toISOString());
    }

    const queryString = searchParams.toString();
    const url = `/api/connections/activity-report${
      queryString ? `?${queryString}` : ""
    }`;

    return api.get<ActivityReportData[]>(url);
  },

  // Actualizar geolocalización
  async updateGeolocation(
    data: UpdateGeolocationRequest
  ): Promise<ApiResponse<any>> {
    return api.post<any>("/api/connections/geolocation", data);
  },

  // Obtener estadísticas en tiempo real
  async getRealtimeStatistics(): Promise<ApiResponse<RealtimeStatistics>> {
    return api.get<RealtimeStatistics>("/api/connections/stats/realtime");
  },

  // Exportar datos de conexiones
  async exportConnectionData(params?: {
    fechaDesde?: Date;
    fechaHasta?: Date;
    formato?: "csv" | "json";
    usuarioId?: number;
  }): Promise<ApiResponse<any>> {
    const searchParams = new URLSearchParams();

    if (params?.fechaDesde) {
      searchParams.append("fechaDesde", params.fechaDesde.toISOString());
    }
    if (params?.fechaHasta) {
      searchParams.append("fechaHasta", params.fechaHasta.toISOString());
    }
    if (params?.formato) {
      searchParams.append("formato", params.formato);
    }
    if (params?.usuarioId) {
      searchParams.append("usuarioId", params.usuarioId.toString());
    }

    const queryString = searchParams.toString();
    const url = `/api/connections/export${
      queryString ? `?${queryString}` : ""
    }`;

    return api.get<any>(url);
  },
};

// Cliente extendido con notificaciones y conexiones
export const apiExtended = {
  ...api,
  notifications,
  connections,
};
